import { useEffect, useState } from "react";
import { setNotificationAtom, voicesDescriptionAtom } from "atoms";
import { useAtomValue, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { getPreviewPrompt, getVoiceDescription } from "shared/utils/tts";
import {
  isQuotaExhaustedError,
  extractQuotaInfo,
  isUsagePeriodExpired,
} from "shared/utils/grpc-error-handler";
import { useTTS } from "./useTTS";

interface UsePlayVoice {
  handlePlayClick: (
    voice: string,
    language: string,
    audioPlayer: HTMLAudioElement | undefined,
    setAudioPlayer: (value: HTMLAudioElement) => void,
    setCurrentPlayingVoiceName: (voiceName: string) => void,
    setProgressVoiceName: (value: string) => void,
    currentPlayingVoiceName: string
  ) => Promise<void>; // Updated return type to Promise<void>
}

export function usePlayVoice(): UsePlayVoice {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const voicesDescription = useAtomValue(voicesDescriptionAtom);
  const setNotification = useSetAtom(setNotificationAtom);
  const { getTTSRequestUrl } = useTTS();

  useEffect(() => {
    if (hasError) {
      setNotification({
        message: errorMessage,
        severity: "error",
      });
      setHasError(false);
    }
  }, [hasError, errorMessage, setNotification]);

  const getPlayContent = (
    voiceName: string,
    language: string
  ): string | undefined => {
    const voiceDescription = getVoiceDescription(voiceName, voicesDescription);

    if (!voiceDescription) {
      // Handle the case where the voice description is not found.
      // eslint-disable-next-line no-console
      console.error(`Voice description not found for voice: ${voiceName}`);
      return undefined;
    }

    // Get the persona-appropriate preview prompt dynamically
    const previewText = getPreviewPrompt(voiceDescription, language);

    return previewText;
  };

  const handlePlayClick = async (
    voice: string,
    language: string,
    audioPlayer: HTMLAudioElement | undefined,
    setAudioPlayer: (value: HTMLAudioElement) => void,
    setCurrentPlayingVoiceName: (voiceName: string) => void,
    setProgressVoiceName: (value: string) => void,
    currentPlayingVoiceName: string
  ): Promise<void> => {
    try {
      if (audioPlayer) {
        audioPlayer.pause();
      }

      // If already playing, reset the voice name and return
      if (voice === currentPlayingVoiceName) {
        setCurrentPlayingVoiceName("");
        return;
      }

      setProgressVoiceName(voice);
      const selectedContent = getPlayContent(voice, language);

      if (!selectedContent) {
        setCurrentPlayingVoiceName("");
        setProgressVoiceName("");
        return;
      }

      const { url } = await getTTSRequestUrl(
        false,
        "",
        selectedContent,
        voice,
        language
      );

      const response = await fetch(url);

      if (!response.ok) {
        const json = await response.json();
        const ttsErrorContent = json.detail.match(/tts: (.*)/)?.[1];
        setHasError(true);
        setErrorMessage(`${t("tts.somethingWentWrong")}: ${ttsErrorContent}`);
        setCurrentPlayingVoiceName("");
        setProgressVoiceName("");
        return;
      }

      const player = new Audio(url);

      player.onended = () => {
        setCurrentPlayingVoiceName("");
      };

      player.onerror = (error) => {
        // eslint-disable-next-line no-console
        console.error("Error playing audio:", error);
        setCurrentPlayingVoiceName("");
        setProgressVoiceName("");
      };

      try {
        await player.play();
        setAudioPlayer(player);
        setCurrentPlayingVoiceName(voice);
        setProgressVoiceName("");
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error playing audio:", error);
        setCurrentPlayingVoiceName("");
        setProgressVoiceName("");
      }
    } catch (error) {
      setHasError(true);

      // Handle quota exhaustion specifically
      if (
        error instanceof Error &&
        (error.message === "QUOTA_EXHAUSTED" || isQuotaExhaustedError(error))
      ) {
        const originalError = (error as { originalError?: unknown })
          .originalError;
        const quotaInfo = extractQuotaInfo(originalError || error);

        if (quotaInfo && isUsagePeriodExpired(quotaInfo)) {
          // Usage period expired (totalQuota = 0.0)
          setErrorMessage(t("tts.usagePeriodExpiredMessage"));
        } else {
          // Fallback to generic message if no quota info available
          setErrorMessage(t("tts.quotaExhaustedMessage"));
        }
      } else {
        setErrorMessage(t("tts.generalErrorMessage"));
      }

      setCurrentPlayingVoiceName("");
      setProgressVoiceName("");
    }
  };

  return { handlePlayClick };
}
